import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  LayoutDashboard,
  FileText,
  LogOut,
  Upload,
  ExternalLink,
  Users,
  Settings,
  Save,
  Info,
  Menu,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmpresaConfiguracoes() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, empresa, logout, refreshEmpresa, isImpersonating, exitImpersonation, loading: authLoading } = useAuth();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [slaDias, setSlaDias] = useState(30);
  const [savingSla, setSavingSla] = useState(false);

  // Load SLA setting from empresa
  useEffect(() => {
    if (empresa?.sla_dias) {
      setSlaDias(empresa.sla_dias);
    }
  }, [empresa]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.error("Sessão expirada. Por favor, faça login novamente.");
      navigate(`/${slug}/login`);
      return;
    }
    
    // Only admins can access settings
    if (user.role !== "admin") {
      toast.error("Acesso restrito a administradores.");
      navigate(`/${slug}/admin`);
      return;
    }
  }, [user, slug, navigate, authLoading]);

  const handleLogout = () => {
    logout();
    navigate(`/${slug}/login`);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("logo", file);
    
    try {
      await axios.put(`${API_URL}/empresas/${slug}/logo`, formData);
      toast.success(t("settings.logoUpdated", "Logo atualizado com sucesso!"));
      if (refreshEmpresa) {
        await refreshEmpresa(slug);
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(t("settings.logoError", "Erro ao atualizar logo"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.put(`${API_URL}/empresas/${slug}/banner`, formData);
      toast.success(t("settings.bannerUpdated", "Banner atualizado com sucesso!"));
      if (refreshEmpresa) {
        await refreshEmpresa(slug);
      }
    } catch (error) {
      console.error("Error uploading banner:", error);
      toast.error(t("settings.bannerError", "Erro ao atualizar banner"));
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSaveSla = async () => {
    if (slaDias < 1 || slaDias > 365) {
      toast.error(t("dashboard.settings.slaError", "SLA deve ser entre 1 e 365 dias"));
      return;
    }
    
    setSavingSla(true);
    try {
      await axios.patch(`${API_URL}/empresas/${slug}/settings`, {
        sla_dias: parseInt(slaDias)
      });
      toast.success(t("dashboard.settings.slaSaved", "Prazo de SLA atualizado com sucesso"));
      if (refreshEmpresa) {
        await refreshEmpresa(slug);
      }
    } catch (error) {
      console.error("Error saving SLA:", error);
      toast.error(error.response?.data?.detail || t("dashboard.settings.slaErrorSave", "Erro ao salvar configurações"));
    } finally {
      setSavingSla(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-4">
          <span>🔐 {t("dashboard.impersonating", "Modo Super Admin - Você está acessando como administrador de")} {empresa?.nome}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={exitImpersonation}
            className="bg-white text-amber-600 hover:bg-amber-50 border-white"
          >
            {t("dashboard.exitImpersonate", "Sair do Modo Impersonate")}
          </Button>
        </div>
      )}

      <div className="flex">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-4 border-b border-slate-200">
              <Link to={`/${slug}/admin`} className="flex items-center gap-3">
                {empresa?.logo_url ? (
                  <img 
                    src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`}
                    alt={empresa?.nome} 
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="font-semibold text-[#1a1a2e]">{empresa?.nome}</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
              <Link
                to={`/${slug}/admin`}
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <LayoutDashboard className="w-5 h-5" />
                {t("dashboard.menu.dashboard")}
              </Link>
              <Link
                to={`/${slug}/admin/casos`}
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <FileText className="w-5 h-5" />
                {t("dashboard.menu.cases")}
              </Link>
              {(user?.role === "admin" || user?.role === "gestor") && (
                <Link
                  to={`/${slug}/admin/investigadores`}
                  className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <Users className="w-5 h-5" />
                  {t("dashboard.menu.investigators")}
                </Link>
              )}
              {user?.role === "admin" && (
                <Link
                  to={`/${slug}/admin/configuracoes`}
                  className="flex items-center gap-3 px-4 py-3 bg-slate-100 text-[#1a1a2e] rounded-lg font-medium"
                >
                  <Settings className="w-5 h-5" />
                  {t("dashboard.menu.settings", "Configurações")}
                </Link>
              )}
            </nav>

            {/* User Info */}
            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                  {user?.nome?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("dashboard.menu.logout")}
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          {/* Mobile Header */}
          <div className="lg:hidden flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="font-semibold text-[#1a1a2e]">{t("dashboard.menu.settings", "Configurações")}</h1>
            <div className="w-10" />
          </div>

          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#1a1a2e]">{t("settings.title", "Configurações")}</h1>
            <p className="text-slate-500">{t("settings.subtitle", "Gerencie as configurações do seu canal de compliance")}</p>
          </div>

          {/* Channel Customization Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="channel-customization">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-[#1a1a2e]">{t("dashboard.customization.title", "Personalização do Canal")}</h3>
                <p className="text-sm text-slate-500">{t("dashboard.customization.subtitle", "Configure a aparência da página pública")}</p>
              </div>
              <Upload className="w-5 h-5 text-slate-400" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Section */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-[#1a1a2e]" />
                  <p className="text-sm font-medium text-[#1a1a2e]">{t("dashboard.customization.logo", "Logo da Empresa")}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden">
                    {empresa?.logo_url ? (
                      <img 
                        src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)}
                        alt="Logo" 
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <label htmlFor="logo-upload">
                      <Button variant="outline" size="sm" asChild className="cursor-pointer">
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingLogo ? t("common.loading") : t("dashboard.customization.uploadLogo", "Alterar Logo")}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-slate-400 mt-2">{t("dashboard.customization.logoTip", "PNG ou JPG, fundo transparente")}</p>
                  </div>
                </div>
              </div>
              
              {/* Banner Section */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink className="w-4 h-4 text-[#f5a623]" />
                  <p className="text-sm font-medium text-[#1a1a2e]">{t("dashboard.customization.banner", "Banner da Página")}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-16 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden">
                    {empresa?.banner_url ? (
                      <img 
                        src={empresa.banner_url.startsWith('data:') ? empresa.banner_url : (empresa.banner_url.startsWith('http') ? empresa.banner_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.banner_url}`)}
                        alt="Banner" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-slate-300">{t("dashboard.customization.noBanner", "Sem banner")}</span>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      id="banner-upload"
                      accept="image/*"
                      onChange={handleBannerUpload}
                      className="hidden"
                    />
                    <label htmlFor="banner-upload">
                      <Button variant="outline" size="sm" asChild className="cursor-pointer">
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingBanner ? t("common.loading") : t("dashboard.customization.uploadBanner", "Alterar Banner")}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-slate-400 mt-2">{t("dashboard.customization.bannerTip", "Tamanho: 1920 x 890 px")}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview Link */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <a 
                href={`/${slug}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#1a1a2e] hover:text-[#f5a623] transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {t("dashboard.customization.preview", "Ver página pública do canal")}
              </a>
            </div>
          </div>

          {/* SLA Settings Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="sla-settings">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-[#1a1a2e]">{t("dashboard.settings.slaTitle", "Prazo de SLA")}</h3>
                <p className="text-sm text-slate-500">{t("dashboard.settings.slaSubtitle", "Configure o prazo máximo para resposta de casos")}</p>
              </div>
              <Settings className="w-5 h-5 text-slate-400" />
            </div>
            
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="sla-dias" className="text-sm text-slate-600 mb-2 block">
                  {t("dashboard.settings.slaDays", "Dias para resposta")}
                </Label>
                <Input
                  id="sla-dias"
                  type="number"
                  min="1"
                  max="365"
                  value={slaDias}
                  onChange={(e) => setSlaDias(parseInt(e.target.value) || 30)}
                  className="w-32"
                  data-testid="sla-input"
                />
              </div>
              <Button 
                onClick={handleSaveSla}
                disabled={savingSla}
                className="bg-[#1a1a2e] hover:bg-[#2a2a3e]"
                data-testid="save-sla-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingSla ? t("common.loading") : t("dashboard.settings.saveSla", "Salvar")}
              </Button>
            </div>
            
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  {t("dashboard.settings.slaInfo", "Casos que ultrapassarem este prazo serão marcados como 'SLA Vencido'. Use o filtro na página de casos para identificá-los facilmente.")}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
